import { beginTask, endTask } from 'redux-nprogress';
import { get, replace, toLower } from 'lodash';
import debug from 'debug';
import routes from '../../../routes.global';
import viewNames from '../../../../store/actions/view.types';
import { loadToolDescriptions } from '../../../../store/reducers/toolDescriptions';

const log = debug('huddle:app:player:');

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'player';
export const selectPlayerState = state => state[STORE_KEY];


// ------------------------------------
// Actions
// ------------------------------------
export const LOAD_PLAYER_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_SUCCESS`;
export const LOAD_PLAYER_ERROR = `${STORE_KEY}/LOAD_PLAYER_ERROR`;
export const LOAD_PLAYER_STATS_SUMMARY_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_STATS_SUMMARY_SUCCESS`;
export const LOAD_PLAYER_STATS_SUMMARY_ERROR = `${STORE_KEY}/LOAD_PLAYER_STATS_SUMMARY_ERROR`;
export const LOAD_PLAYER_NEWS_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_NEWS_SUCCESS`;
export const LOAD_PLAYER_NEWS_ERROR = `${STORE_KEY}/LOAD_PLAYER_NEWS_ERROR`;
export const LOAD_PLAYER_STATS_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_STATS_SUCCESS`;
export const LOAD_PLAYER_STATS_ERROR = `${STORE_KEY}/LOAD_PLAYER_STATS_ERROR`;
export const LOAD_PLAYER_GAMELOGS_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_GAMELOGS_SUCCESS`;
export const LOAD_PLAYER_GAMELOGS_ERROR = `${STORE_KEY}/LOAD_PLAYER_GAMELOGS_ERROR`;
export const LOAD_PLAYER_ROSTER_SUCCESS = `${STORE_KEY}/LOAD_PLAYER_ROSTER_SUCCESS`;
export const LOAD_PLAYER_ROSTER_ERROR = `${STORE_KEY}/LOAD_PLAYER_ROSTER_ERROR`;

export const CLEAR_PLAYER = `${STORE_KEY}/CLEAR_PLAYER`;
export const SET_PLAYER_FORMULA = `${STORE_KEY}/SET_PLAYER_FORMULA`;
export const SET_PLAYER_SEASON = `${STORE_KEY}/SET_PLAYER_SEASON`;
export const SET_PLAYER_SUBVIEW = `${STORE_KEY}/SET_PLAYER_SUBVIEW`;


// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
  currentSubview: null,
  currentSeason: null,
  currentFormula: null,
  description: null,
  summary: null,
  news: null,
  roster: null,
  stats: null,
  statsColMappings: null,
  gameLogs: null,
  gameLogsColMappings: null,
  playerDataHasLoaded: false,
  playerRosterHasLoaded: false,
  playerStatsSummaryHasLoaded: false,
  playerNewsHasLoaded: false,
  playerStatsHasLoaded: false,
  playerGameLogsHasLoaded: false,
  error: null,
};

const playerReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_PLAYER_FORMULA:
    case SET_PLAYER_SEASON:
    case SET_PLAYER_SUBVIEW:
      return { ...state, ...action.payload };
    case CLEAR_PLAYER:
      return { ...state, ...initialState };
    case LOAD_PLAYER_SUCCESS:
      return { ...state, ...action.payload, playerDataHasLoaded: true };
    case LOAD_PLAYER_STATS_SUMMARY_SUCCESS:
      return { ...state, ...action.payload, playerStatsSummaryHasLoaded: true };
    case LOAD_PLAYER_NEWS_SUCCESS:
      return { ...state, ...action.payload, playerNewsHasLoaded: true };
    case LOAD_PLAYER_STATS_SUCCESS:
      return { ...state, ...action.payload, playerStatsHasLoaded: true };
    case LOAD_PLAYER_GAMELOGS_SUCCESS:
      return { ...state, ...action.payload, playerGameLogsHasLoaded: true };
    case LOAD_PLAYER_ROSTER_SUCCESS:
      return { ...state, ...action.payload, playerRosterHasLoaded: true };
    case LOAD_PLAYER_GAMELOGS_ERROR: {
      const { gameLogs, playerGameLogsHasLoaded } = initialState;
      return { ...state, gameLogs, playerGameLogsHasLoaded };
    }
    case LOAD_PLAYER_NEWS_ERROR:
    case LOAD_PLAYER_STATS_ERROR:
    case LOAD_PLAYER_ERROR:
    case LOAD_PLAYER_STATS_SUMMARY_ERROR:
    case LOAD_PLAYER_ROSTER_ERROR:
      return { ...state, error: action.payload.error };
    default:
      return state;
  }
};

export default playerReducer;
// ------------------------------------
// Update season/formula/view
// ------------------------------------
export const setPlayerFormula = formula => ({
  type: SET_PLAYER_FORMULA,
  payload: { currentFormula: formula },
});

export const setPlayerSeason = season => ({
  type: SET_PLAYER_SEASON,
  payload: { currentSeason: season },
});

export const setPlayerSubview = subview => ({
  type: SET_PLAYER_SUBVIEW,
  payload: { currentSubview: subview },
});

// ------------------------------------
// Load Player Data
// ------------------------------------
export const clearPlayer = () => ({
  type: CLEAR_PLAYER,
});

export const loadPlayerSuccess = ({ description }) => ({
  type: LOAD_PLAYER_SUCCESS,
  payload: { description },
});

export const loadPlayerError = error => ({
  type: LOAD_PLAYER_ERROR,
  payload: { error },
});

export const loadPlayerStatsSummarySuccess = ({ summary, currentFormula }) => ({
  type: LOAD_PLAYER_STATS_SUMMARY_SUCCESS,
  payload: { summary, currentFormula },
});

export const loadPlayerStatsSummaryError = error => ({
  type: LOAD_PLAYER_STATS_SUMMARY_ERROR,
  payload: { error },
});

export const loadPlayerNewsSuccess = ({ news }) => ({
  type: LOAD_PLAYER_NEWS_SUCCESS,
  payload: { news },
});

export const loadPlayerNewsError = error => ({
  type: LOAD_PLAYER_NEWS_ERROR,
  payload: { error },
});

export const loadTeamRosterSuccess = ({ roster }) => ({
  type: LOAD_PLAYER_ROSTER_SUCCESS,
  payload: { roster },
});

export const loadTeamRosterError = error => ({
  type: LOAD_PLAYER_ROSTER_ERROR,
  payload: { error },
});

export const loadPlayerStatsSuccess = ({ stats, statsColMappings }) => ({
  type: LOAD_PLAYER_STATS_SUCCESS,
  payload: { stats, statsColMappings },
});

export const loadPlayerStatsError = error => ({
  type: LOAD_PLAYER_STATS_ERROR,
  payload: { error },
});

export const loadPlayerGameLogsSuccess = ({ gameLogs, gameLogsColMappings }) => ({
  type: LOAD_PLAYER_GAMELOGS_SUCCESS,
  payload: { gameLogs, gameLogsColMappings },
});

export const loadPlayerGameLogsError = error => ({
  type: LOAD_PLAYER_GAMELOGS_ERROR,
  payload: { error },
});

export const loadTeamRoster = teamId => async (dispatch, getState, { api }) => {
  try {
    const { roster } = await api.players.playerTeamRoster({ teamId });
    dispatch(loadTeamRosterSuccess({ roster }));
  } catch (error) {
    log(error);
    dispatch(loadTeamRosterError(error));
  }
};

export const loadPlayerData = id => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    const { player: description } = await api.players.playerDescription({ id });
    dispatch(loadPlayerSuccess({ description }));
    await dispatch(loadTeamRoster(description.team_id));
  } catch (error) {
    log(error);
    dispatch(loadPlayerError(error));
  } finally {
    dispatch(endTask());
  }
};

export const loadPlayerStatsSummary = (id, formula, year) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    const { summary } = await api.players.playerStatsSummary({ id, formula, year });
    dispatch(loadPlayerStatsSummarySuccess({ summary, currentFormula: formula }));
  } catch (error) {
    log(error);
    dispatch(loadPlayerStatsSummaryError(error));
  }
};

export const loadPlayerNews = id => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    const { news } = await api.players.playerNews({ id });
    dispatch(loadPlayerNewsSuccess({ news }));
  } catch (error) {
    log(error);
    dispatch(loadPlayerNewsError(error));
  } finally {
    dispatch(endTask());
  }
};

export const loadPlayerStats = (id, formula) => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    const [{ player_stats: stats }, { mappings: statsColMappings }] =
      await Promise.all([
        api.players.playerStats({ id, formula }),
        api.players.playerStatsColMappings(),
      ]);
    dispatch(loadPlayerStatsSuccess({ stats, statsColMappings }));
  } catch (error) {
    log(error);
    dispatch(loadPlayerStatsError(error));
  } finally {
    dispatch(endTask());
  }
};

export const loadPlayerGameLogs = (id, year, formula) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    dispatch(beginTask());
    const [{ game_log: gameLogs }, { mappings: gameLogsColMappings }] =
      await Promise.all([
        api.players.playerGameLogs({ id, year, formula }),
        api.players.playerGameLogsColMappings(),
      ]);
    dispatch(loadPlayerGameLogsSuccess({ gameLogs, gameLogsColMappings }));
  } catch (error) {
    log(error);
    dispatch(loadPlayerGameLogsError(error));
  } finally {
    dispatch(endTask());
  }
};

export const loadNewFormula = formula => async (dispatch, getState) => {
  const state = getState();
  const { id } = state.player.description;
  const { currentSeason } = state.player;
  dispatch(setPlayerFormula(formula));
  await Promise.all([
    dispatch(loadPlayerStatsSummary(id, formula, currentSeason)),
    dispatch(loadPlayerStats(id, formula)),
    dispatch(loadPlayerGameLogs(id, currentSeason, formula)),
  ]);
};

export const loadNewSeason = season => async (dispatch, getState) => {
  const state = getState();
  const { id } = state.player.description;
  const { currentFormula } = state.player;
  dispatch(setPlayerSeason(season));
  await Promise.all([
    dispatch(loadPlayerGameLogs(id, season, currentFormula)),
    dispatch(loadPlayerStatsSummary(id, currentFormula, season)),
  ]);
};

// ------------------------------------
// View / Route
// ------------------------------------
export const PLAYER_VIEW = 'player/VIEW';
viewNames[PLAYER_VIEW] = 'Player';
routes[PLAYER_VIEW] = {
  // Regex check to ensure id is a number
  path: '/player/:id(\\d+)/:name',
  thunk: async (dispatch, getState) => {
    const state = getState();
    const { id } = state.location.payload;
    // Lodash get() avoids error on accessing nested properties
    const defaultSeason = get(state, 'seasonLookup.current.stats.season');
    const { currentFormula, currentSeason } = state.player;
    const { defaultFormula } = state.formulas;

    // These values can't be zero, safe to use short-circuit
    const formula = currentFormula || defaultFormula || 1;
    const year = currentSeason || defaultSeason || 2016;
    dispatch(clearPlayer());
    dispatch(setPlayerSubview('news'));
    dispatch(setPlayerFormula(formula));
    dispatch(setPlayerSeason(year));
    await dispatch(loadPlayerData(id));
    await Promise.all([
      dispatch(loadPlayerStatsSummary(id, formula, year)),
      dispatch(loadPlayerNews(id)),
      dispatch(loadPlayerStats(id, formula)),
      dispatch(loadPlayerGameLogs(id, year, formula)),
      dispatch(loadToolDescriptions()),
    ]);
  },
};

export const PLAYER_VIEW_SUBVIEW = 'player/VIEW_SUBVIEW';
viewNames[PLAYER_VIEW_SUBVIEW] = 'Player';
routes[PLAYER_VIEW_SUBVIEW] = {
  path: '/player/:subview/:id(\\d+)/:name',
  thunk: async (dispatch, getState) => {
    const state = getState();
    const { id, subview } = state.location.payload;
    // Lodash get() avoids error on accessing nested properties
    const currentId = get(state, 'player.description.id');
    const defaultSeason = get(state, 'seasonLookup.current.stats.season');
    const { currentFormula } = state.player;
    const { defaultFormula } = state.formulas;
    const { currentSeason } = state.player;

    // These values can't be zero, safe to use short-circuit
    const formula = currentFormula || defaultFormula || 1;
    const year = currentSeason || defaultSeason || 2016;

    if (subview === 'news' || subview === 'stats' || subview === 'logs') {
      if (currentId === id) dispatch(setPlayerSubview(subview));
      else {
        dispatch(clearPlayer());
        dispatch(setPlayerSubview(subview));
        dispatch(setPlayerFormula(formula));
        dispatch(setPlayerSeason(year));
        await dispatch(loadPlayerData(id));
        await Promise.all([
          dispatch(loadPlayerStatsSummary(id, formula, year)),
          dispatch(loadPlayerNews(id)),
          dispatch(loadPlayerStats(id, formula)),
          dispatch(loadPlayerGameLogs(id, year, formula)),
          dispatch(loadToolDescriptions()),
        ]);
      }
    }
  },
};

export const goPlayer = (id, name) => ({ type: PLAYER_VIEW, payload: { id, name: toLower(replace(name, /\./g, '')) } });

export const goPlayerSubview = ({ id, subview, name }) => ({
  type: PLAYER_VIEW_SUBVIEW,
  payload: { id, subview, name },
});



// WEBPACK FOOTER //
// ./src/app/views/Player/module/player.js